// dependencies
import produce from "immer";
import { isImmutable } from "immutable";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import Chip from "../../../components/Chip";
import PageTitle from "../../../components/PageTitle";
import Paginator from "../../../components/Pagination";
import TableLite from "../../../components/TableLite";
// components
import Filters from "./Filters";

import useQueryParams from "../../../hooks/useQueryParams/useQueryParams";
// hooks
import useRequest from "../../../hooks/useRequest";

// selectors
import { getUserCompanyName } from "../../../selectors/app";

// utils
import get from "../../../utils/GetNested";
import getPrettyIntegrationName from "../../../utils/getPrettyIntegrationName";

// styles
import css from "./Log.module.scss";
// config
import config from "./config";

const transformQuery = (query) => {
	const updatedQuery = produce(query, (draftState) => {
		const status = get(draftState, ["filters", "status"], null);

		// if status is all, then there really isn't a filter for
		// status, so remove it.
		if (status === "all") {
			delete draftState.filters.status;
		}

		// parse the other keys
		Object.keys(draftState.filters).forEach((key) => {
			const value = draftState.filters[key];
			if (isImmutable(value)) {
				if (key === "employee") {
					draftState.filters[key] = value.get("id", "");
				}
			}
		});
	});

	return updatedQuery;
};

const Log = () => {
	const company = useSelector(getUserCompanyName);
	const { queryState, setQueryState, isLoading: isParsingParams } = useQueryParams(config.queryParams);

	const filters = {
		status: get(queryState, ["status", "value"], "all"),
	};

	const [currentPage, setCurrentPage] = useState(1);

	// set the initial sort
	const [currentSort, setCurrentSort] = useState({
		name: "finished_at",
		direction: "desc",
	});

	const limit = config.pageSize;
	const skip = (currentPage - 1) * limit;

	const { data, isRequesting, revalidate } = useRequest(
		"getLog",
		{
			filters,
			skip,
			limit,
			sort: {
				name: currentSort.name,
				direction: currentSort.direction,
			},
		},
		{
			manual: true,
			onBeforeRequest: transformQuery,
			skipCache: true,
		}
	);

	useEffect(() => {
		if (!isParsingParams) {
			revalidate();
		}
	}, [isParsingParams, filters.status, limit, skip, currentSort.name, currentSort.direction]);

	const logs = get(data, ["integrationLogs"], []).map((log) => ({
		id: log._id,
		company_id: log.company_id,
		csv_properties: log.csv_properties,
		errors: log.errors,
		finished_at: log.finished_at,
		integration_type: log.integration_type,
		sftp_properties: log.sftp_properties,
		total_user_updates: log.total_user_updates,
		start_at: log.start_at,
		user_updates: log.user_updates,
		users_failed_sync: log.users_failed_sync,
		users_successfully_synced: log.users_successfully_synced,
		users_with_no_changes: log.users_with_no_changes,
		users_created: log.users_created,
		users_deactivated: log.users_deactivated,
		user_deactivation_approved: log.user_deactivation_approved,
		user_deactivation_dismissed: log.user_deactivation_dismissed,
		requires_approval_to_deactivate: log.requires_approval_to_deactivate,
		integrationProperties: log.integrationProperties,
		connect_worker_properties: log.connect_worker_properties,
		fetchedEmployees: log.fetchedEmployees,
		updatedIntegrationWorkers: log.updatedIntegrationWorkers,
		hoverRowURL: `/administration/sync-log/${log._id}`,
		is_processing: log.is_processing,
	}));

	// change the current sorted column
	const handleSortColumn = (columnID, direction) => {
		setCurrentSort({ name: columnID, direction });
	};

	const getStatus = (log) => {
		const sftpError = log.errors.length > 0;
		const usersFailedSync = log.users_failed_sync > 0;
		const deactivateRequiresApproval = get(log, ["requires_approval_to_deactivate"], []);
		const deactivationApproved = get(log, ["user_deactivation_approved"], false);
		const deactivationDismissed = get(log, ["user_deactivation_dismissed"], false);
		const isProcessing = get(log, ["is_processing"], false);

		if (isProcessing) {
			return <Chip label="Processing" color="default" />;
		}

		if (
			!deactivationApproved &&
			!deactivationDismissed &&
			deactivateRequiresApproval &&
			deactivateRequiresApproval.length > 0
		) {
			return <Chip label="Pending Deactivation" color="orange" />;
		}

		if (sftpError) {
			return <Chip label="Failed" color="red" />;
		}

		if (!sftpError && usersFailedSync) {
			return <Chip label="Completed with Errors" color="yellow" />;
		}

		if (!sftpError && !usersFailedSync) {
			return <Chip label="Completed" color="green" />;
		}

		return "";
	};

	const columns = [
		{
			id: "status",
			label: "Status",
			isSortable: false,
			width: "150px",
			getter: (log) => <div>{getStatus(log)}</div>,
		},
		{
			id: "event",
			label: "Name",
			isSortable: false,
			width: "1fr",
			getter: (log) => {
				let value = "";
				const type = log.integration_type;

				if (type === "csv" || type === "sftp-sync") {
					value = `SFTP Sync: ${get(
						log,
						["sftp_properties", "sftp_integration_name", "name"],
						"CSV Upload"
					)}`;
				} else if (type === "csv-upload") {
					value = "CSV Upload";
				} else if (type === "connect-workers") {
					let integrationName = get(log, ["integrationProperties", "integrationName"], "");
					const prettyIntegrationName = getPrettyIntegrationName(integrationName);
					value = `${prettyIntegrationName} Integration Settings Update`;
				} else if (type === "integration-sync") {
					value = `${getPrettyIntegrationName(
						get(log, ["integrationProperties", "integrationName"], "")
					)} Integration`;
				}

				return <div className={css.eventName}>{value}</div>;
			},
		},
		{
			id: "testingMode",
			label: "",
			isSortable: false,
			width: "100px",
			getter: (log) => {
				const isTestingMode = get(log, ["sftp_properties", "testing_mode"], false);
				return <div>{isTestingMode ? <Chip label="Testing Mode" color="orange" /> : null}</div>;
			},
		},
		{
			id: "numOfUsers",
			label: "Total Employees",
			isSortable: false,
			width: "1fr",
			centerHeader: true,
			getter: (log) => {
				let value;

				value = get(log, ["csv_properties", "num_users_in_csv"], "");

				if (value === "") {
					value = get(log, ["integrationProperties", "connectedEmployeesCount"], "");
				}

				if (value === "") {
					value = get(log, ["connect_worker_properties", "numUsersAttemptedToSync"], "");
				}

				return <div style={{ textAlign: "center" }}>{value}</div>;
			},
		},
		{
			id: "synced",
			label: "Updated Employees",
			isSortable: false,
			width: "1fr",
			centerHeader: true,
			getter: (log) => <div style={{ textAlign: "center" }}>{get(log, ["users_successfully_synced"], "")}</div>,
		},
		{
			id: "deactivated",
			label: "Deactivated Employees",
			isSortable: false,
			width: "1fr",
			centerHeader: true,
			getter: (log) => <div style={{ textAlign: "center" }}>{get(log, ["users_deactivated"], []).length}</div>,
		},
		{
			id: "failed",
			label: "Failed Employees",
			isSortable: false,
			width: "1fr",
			centerHeader: true,
			getter: (log) => <div style={{ textAlign: "center" }}>{get(log, ["users_failed_sync"], "")}</div>,
		},
		{
			id: "noUpdates",
			label: "Employees Without Updates",
			isSortable: false,
			width: "1fr",
			centerHeader: true,
			getter: (log) => <div style={{ textAlign: "center" }}>{get(log, ["users_with_no_changes"], "")}</div>,
		},
		{
			id: "finishedAt",
			label: "Completed At",
			sortName: "finished_at",
			width: "1fr",
			getter: (log) => <div>{log.finished_at ? moment(log.finished_at).format("MMM Do YYYY h:mm a") : null}</div>,
		},
	];

	const handleChangePage = (nextPage) => {
		setCurrentPage(nextPage);
	};

	return (
		<AdminContainer>
			<div className={css.sftpLogContainer}>
				<PageTitle title={`Bulk User Logs - ${company} - PerformYard`} />
				<AdminTitle>Bulk User Logs</AdminTitle>
				{data?.integrationLogs ? (
					<div>
						<div className={css.inputsContainer}>
							<Filters filters={filters} setQueryState={setQueryState} />
						</div>
						<TableLite
							isLoading={isRequesting}
							columns={columns}
							rows={logs}
							onColumnClick={handleSortColumn}
							activeSort={currentSort.name}
							activeSortDirection={currentSort.direction}
						/>
						{data.totalCount > config.pageSize ? (
							<div className={css.paginatorWrapper}>
								<Paginator
									pageCount={Math.ceil(data.totalCount / config.pageSize)}
									currentPage={currentPage}
									onClick={handleChangePage}
								/>
							</div>
						) : null}
					</div>
				) : null}
			</div>
		</AdminContainer>
	);
};

export default Log;
